import { BaseApi } from './base.api.js';
import { ApiError } from '../errors/api-error.js';

export class AssetGroupMeasureApplicabilityApi extends BaseApi {
    constructor(client) {
        super(client, '/asset-group-measure-applicabilities');
    }

    async patch(id, data) {
        try {
            const response = await this.client.patch(`${this.basePath}/${id}`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getLinkedMeasures(id, params = {}) {
        try {
            const response = await this.client.get(`${this.basePath}/${id}/linked-measures`, { params });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getMeasureEvaluation(id, measureId) {
        try {
            const response = await this.client.get(`${this.basePath}/${id}/measure-evaluations/${measureId}`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async patchMeasureEvaluation(id, measureId, data) {
        try {
            const response = await this.client.patch(`${this.basePath}/${id}/measure-evaluations/${measureId}`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async patchCurrentSituationMeasureEvaluation(id, measureId, data) {
        try {
            const response = await this.client.patch(
                `${this.basePath}/${id}/measure-evaluations/${measureId}/current-situation-evaluation`,
                data,
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getPriorMeasureEvaluations(id, measureId) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, '/prior-evaluations'),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getPriorMeasureEvaluationDetail(id, measureId, sourceApplicabilityId) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, `/prior-evaluations/${sourceApplicabilityId}`),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getPriorEvaluationRoadMapWorkPackages(id, measureId, sourceApplicabilityId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(
                    id,
                    measureId,
                    `/prior-evaluations/${sourceApplicabilityId}/road-map-work-packages`,
                ),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getPriorEvaluationCompensatoryControls(id, measureId, sourceApplicabilityId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(
                    id,
                    measureId,
                    `/prior-evaluations/${sourceApplicabilityId}/compensatory-controls`,
                ),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    _measureEvalPath(id, measureId, suffix = '') {
        return `${this.basePath}/${id}/measure-evaluations/${measureId}${suffix}`;
    }

    async getLinkedRoadMapWorkPackages(id, measureId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, '/road-map-work-packages'),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getAvailableRoadMapWorkPackages(id, measureId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, '/road-map-work-packages/available'),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async linkRoadMapWorkPackages(id, measureId, ids) {
        try {
            const response = await this.client.post(
                this._measureEvalPath(id, measureId, '/road-map-work-packages/link'),
                { ids },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async unlinkRoadMapWorkPackage(id, measureId, workPackageId) {
        try {
            const response = await this.client.delete(
                this._measureEvalPath(id, measureId, `/road-map-work-packages/${workPackageId}`),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async bulkUnlinkRoadMapWorkPackages(id, measureId, ids) {
        try {
            const response = await this.client.post(
                this._measureEvalPath(id, measureId, '/road-map-work-packages/bulk-unlink'),
                { ids },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getLinkedCompensatoryControls(id, measureId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, '/compensatory-controls'),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getAvailableCompensatoryControls(id, measureId, params = {}) {
        try {
            const response = await this.client.get(
                this._measureEvalPath(id, measureId, '/compensatory-controls/available'),
                { params },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async linkCompensatoryControls(id, measureId, ids) {
        try {
            const response = await this.client.post(
                this._measureEvalPath(id, measureId, '/compensatory-controls/link'),
                { ids },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async unlinkCompensatoryControl(id, measureId, compensatoryControlId) {
        try {
            const response = await this.client.delete(
                this._measureEvalPath(id, measureId, `/compensatory-controls/${compensatoryControlId}`),
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async bulkUnlinkCompensatoryControls(id, measureId, ids) {
        try {
            const response = await this.client.post(
                this._measureEvalPath(id, measureId, '/compensatory-controls/bulk-unlink'),
                { ids },
            );
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }
}
